import Phaser from 'phaser';
import { COLORS, FONTS, GAME_WIDTH, TIER_COLORS, fontStr, microFontStr } from '@/config';
import { lagRundetRekt, type RundetRektProxy } from '@/ui/RoundedRect';
import { Kolonne } from '@/ui/layout';
import { gameState } from '@/state/store';
import type { GameStateData, Tier } from '@/state/GameState';
import { SESONG_NAVN, aaret, sesongForDag } from '@/systems/sesong';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';
import { ariaMirror } from '@/ui/AriaMirror';
import { audioManager } from '@/systems/audioManager';
import { ambientAudio } from '@/systems/ambientAudio';
import { autoSpeed, type AutoSpeedNivaa } from '@/systems/autoSpeed';
import { sfx } from '@/systems/sfx';
import { gameClock, klokkeBus } from '@/systems/gameClock';

// Runde 47: HUD rullet tilbake til ren Phaser-rendering. DOM-overlayet
// (HudDom) er fjernet siden DOM-koordinater og Phaser-koordinater (1080×1920
// FIT) ikke holdt seg synk på tvers av viewport-størrelser, noe som ga
// massive overlapp mellom DOM-HUD og Phaser-tekst.
//
// HUD-zonen er nå 96 px høy i Phaser-koordinater og inneholder alt:
// kroner, dag, sesong, tier-chip, omdømme-chip, mute-knapp og speed-knapp.
const HUD_HEIGHT = 96;

// Tier-chip-er bruker samme palett som elsewhere i UI-en. S og A får
// spesial-bakgrunn (gull / dyp-lilla) for ekstra glans på toppen.
const TIER_BG: Record<Tier, number> = {
  D: 0x6b6b6b,
  C: 0x8b5e3c,
  B: 0x862736,
  A: 0x4a2c4f,
  S: 0xb8860b,
};

const TIER_TEKST_FARGE: Record<Tier, string> = {
  D: '#ffffff',
  C: '#ffffff',
  B: '#ffffff',
  A: '#ffffff',
  S: '#3a2010',
};

function omdommeNivaaTekst(verdi: number): string {
  if (verdi >= 85) return 'Stjerne';
  if (verdi >= 60) return 'Anerkjent';
  if (verdi >= 30) return 'Etablert';
  return 'Ny';
}

function formaterKroner(n: number): string {
  return `${n.toLocaleString('nb-NO')} kr`;
}

function sesongTekst(dag: number): string {
  const s = sesongForDag(dag);
  return `${SESONG_NAVN[s]} år ${aaret(dag)}`;
}

/**
 * HUDScene — alle HUD-elementer rendres i Phaser slik at koordinatene
 * holder seg synk med scene-innholdet. Skjermlesere får fortsatt info
 * via `ariaMirror.varselHud(...)` (skjult ARIA-live-region i DOM) og
 * via HiddenActions-knappene som triggrer samme handler som mute-knappen.
 */
export class HUDScene extends Phaser.Scene {
  private kronerTekst?: Phaser.GameObjects.Text;
  private klokkeTekst?: Phaser.GameObjects.Text;
  private dagTekst?: Phaser.GameObjects.Text;
  private sesongTekst?: Phaser.GameObjects.Text;

  private tierChipBg?: RundetRektProxy;
  private tierChipTekst?: Phaser.GameObjects.Text;
  private tierGlow?: RundetRektProxy;
  private tierGlowTween?: Phaser.Tweens.Tween;

  private omdommeChipBg?: RundetRektProxy;
  private omdommeChipTekst?: Phaser.GameObjects.Text;

  private muteKnappBg?: RundetRektProxy;
  private muteKnappTekst?: Phaser.GameObjects.Text;
  private speedKnappBg?: RundetRektProxy;
  private speedKnappTekst?: Phaser.GameObjects.Text;
  private settingsKnappBg?: RundetRektProxy;

  // Kroner-telleren animeres (count-up + fargeblink) i stedet for å hoppe.
  // visKroner holder verdien som vises akkurat nå, slik at en ny endring
  // kan telle videre fra der animasjonen faktisk står.
  private visKroner = 0;
  private kronerInit = false;
  private kronerTween?: Phaser.Tweens.Tween;
  private kronerFargeTimer?: Phaser.Time.TimerEvent;

  private unsubscribe?: () => void;
  private unsubAutoSpeed?: () => void;
  private unsubKlokke?: () => void;

  constructor() {
    super('HUD');
  }

  create(): void {
    // Beige bakgrunns-stripe under HUD med skille-linje mot scene-innhold.
    this.add
      .rectangle(0, 0, GAME_WIDTH, HUD_HEIGHT, COLORS.beige)
      .setOrigin(0, 0)
      .setAlpha(0.96);
    this.add
      .rectangle(0, HUD_HEIGHT, GAME_WIDTH, 4, COLORS.wine)
      .setOrigin(0, 0)
      .setAlpha(0.9);

    this.byggStatRad();
    this.byggChipRad();
    this.byggKnapper();

    this.refresh(gameState.snapshot);
    this.unsubscribe = gameState.subscribe((snap) => this.refresh(snap));
    this.unsubAutoSpeed = autoSpeed.subscribe((n) => this.oppdaterSpeedSymbol(n));
    // Klokka oppdateres på hver tidsbruk-hendelse — den drives av
    // handlinger (GameClock), ikke av gameState-snapshots. Spilltest-
    // funn: tidskostnadene var usynlige — vis flytende «−X min»-
    // kvittering under klokka for hver handling.
    this.unsubKlokke = klokkeBus.on('time:advanced', (h) => {
      this.oppdaterKlokke();
      this.visTidsDelta(h.minutter, h.handling);
    });
    this.oppdaterKlokke();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
      this.unsubAutoSpeed?.();
      this.unsubAutoSpeed = undefined;
      this.unsubKlokke?.();
      this.unsubKlokke = undefined;
      this.tierGlowTween?.stop();
      this.tierGlowTween = undefined;
      this.kronerTween?.stop();
      this.kronerTween = undefined;
      this.kronerFargeTimer?.remove();
      this.kronerFargeTimer = undefined;
    });
  }

  /**
   * Pulserer tier-chip-en med gull-glød. Brukes når tier oppgraderes.
   * Respekterer "redusert bevegelse"-innstillingen.
   */
  glodTierChip(varighetMs = 3000): void {
    if (!this.tierGlow) return;
    if (foretrekkerRedusertBevegelse()) return;
    this.tierGlowTween?.stop();
    this.tierGlow.setFillStyle(0xb8860b, 0);
    const proxy = { v: 0 };
    this.tierGlowTween = this.tweens.add({
      targets: proxy,
      v: { from: 0, to: 0.85 },
      duration: 500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: Math.max(0, Math.floor(varighetMs / 1000) - 1),
      onUpdate: () => {
        this.tierGlow?.setFillStyle(0xb8860b, proxy.v);
      },
      onComplete: () => {
        this.tierGlow?.setFillStyle(0xb8860b, 0);
      },
    });
  }

  // ---------- Bygg ----------

  /**
   * Øverste rad: fire stat-bokser (kroner, klokke, dag, sesong) jevnt
   * fordelt fra venstre. Hver boks har label + verdi. Klokka kom inn i
   * fase 1 av Mad TV-mekanikkene — tid er nå en ressurs.
   */
  private byggStatRad(): void {
    const padding = 22;
    const radY = 10;
    const boksHoyde = 48;
    // Reserverer plass til tre knapper (speed + mute + settings) helt til høyre.
    const knapperBredde = 3 * 56 + 2 * 12 + padding;
    const tilgjengelig = GAME_WIDTH - padding - knapperBredde;
    const boksBredde = Math.floor((tilgjengelig - 3 * 12) / 4);

    const lagStat = (i: number, label: string): Phaser.GameObjects.Text => {
      const x = padding + i * (boksBredde + 12);
      const bg = lagRundetRekt(this, x, radY, boksBredde, boksHoyde, COLORS.white, 0.94, 12);
      bg.setStrokeStyle(1, COLORS.oakDark, 0.25);

      // Mikro-label (2026-06-04): «KRONER»/«DAG»/«SESONG» bruker microFontStr
      // (~14px) i stedet for fontStr(11)=22px. Det doblet etiketten og presset
      // verdien ned i chip-raden. Label + verdi stables nå fra målt høyde, så
      // de aldri overlapper og verdien får klaring til chip-raden under.
      const labelTxt = this.add
        .text(x + 10, 0, label, {
          fontFamily: FONTS.hud,
          fontSize: microFontStr(11),
          color: '#8b5e3c',
          fontStyle: '700',
        })
        .setOrigin(0, 0);

      const verdi = this.add
        .text(x + 10, 0, '', {
          fontFamily: FONTS.display,
          fontSize: fontStr(20),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(0, 0);

      const kol = new Kolonne(x + 10, radY + 2, 0);
      kol.legg(labelTxt);
      kol.legg(verdi);
      return verdi;
    };

    this.kronerTekst = lagStat(0, 'KRONER');
    this.klokkeTekst = lagStat(1, 'KLOKKE');
    this.dagTekst = lagStat(2, 'DAG');
    this.sesongTekst = lagStat(3, 'SESONG');
  }

  /**
   * Oppdaterer klokkevisningen fra GameClock. Diskret varsel: tallet
   * skifter til varselrødt når det er under én time igjen av dagen.
   */
  private oppdaterKlokke(): void {
    if (!this.klokkeTekst) return;
    this.klokkeTekst.setText(gameClock.klokkeslett);
    this.klokkeTekst.setColor(gameClock.underEnTimeIgjen ? '#b3261e' : '#1a1a1a');
  }

  /** Flytende «−30 min · vurderte et manus» under klokke-boksen. */
  private visTidsDelta(minutter: number, handling?: string): void {
    if (minutter <= 0 || !this.klokkeTekst) return;
    const tekst = handling ? `−${minutter} min · ${handling}` : `−${minutter} min`;
    const flyt = this.add
      .text(this.klokkeTekst.x, 100 + 14, tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(15),
        color: '#8B5E3C',
        fontStyle: '700',
        backgroundColor: 'rgba(253,250,243,0.9)',
        padding: { left: 8, right: 8, top: 3, bottom: 3 },
      })
      .setOrigin(0, 0)
      .setDepth(60);
    if (foretrekkerRedusertBevegelse()) {
      this.tweens.add({
        targets: flyt,
        alpha: { from: 1, to: 0 },
        duration: 1100,
        delay: 700,
        onComplete: () => flyt.destroy(),
      });
      return;
    }
    flyt.setAlpha(0);
    this.tweens.add({
      targets: flyt,
      y: 100 + 14 + 26,
      alpha: { from: 1, to: 0 },
      duration: 1600,
      ease: 'Sine.easeOut',
      onComplete: () => flyt.destroy(),
    });
  }

  /**
   * Nederste rad i HUD-zonen: tier-chip + omdømme-chip på venstre side.
   * Tier-chip dispatcher 'hud:openTier' på document slik at
   * ForlagshusetScene kan åpne MinKarriereModal — samme mønster som
   * tidligere DOM-HUD brukte.
   */
  private byggChipRad(): void {
    const padding = 22;
    const radY = 62;
    const chipH = 28;

    // Skjult gull-glow bak tier-chip — brukes ved tier-oppgradering.
    const tierW = 110;
    const tierX = padding;
    this.tierGlow = lagRundetRekt(this, tierX - 4, radY - 4, tierW + 8, chipH + 8, 0xb8860b, 0, 14);

    this.tierChipBg = lagRundetRekt(this, tierX, radY, tierW, chipH, TIER_BG.D, 1, 14);
    this.tierChipBg.setStrokeStyle(2, COLORS.white, 0.9);
    this.tierChipTekst = this.add
      .text(tierX + tierW / 2, radY + chipH / 2, 'Tier D', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(13),
        color: TIER_TEKST_FARGE.D,
        fontStyle: '700',
      })
      .setOrigin(0.5);

    this.tierChipBg.setInteractive({ useHandCursor: true });
    this.tierChipBg.on('pointerup', () => {
      sfx.play('click');
      document.dispatchEvent(new CustomEvent('hud:openTier'));
    });

    // Omdømme-chip — viser nivå-navn. Klikk er passivt (kun fokus),
    // ekte detaljvisning ligger i Min karriere-modalen.
    // Runde 48 (Sven): flyttet litt til høyre (gap 10→18) og bredere
    // (200→240) så «Omdømme: Etablert (50)» får plass uten å flyte utenfor.
    const omdW = 240;
    const omdX = tierX + tierW + 18;
    this.omdommeChipBg = lagRundetRekt(this, omdX, radY, omdW, chipH, COLORS.white, 0.94, 14);
    this.omdommeChipBg.setStrokeStyle(1, COLORS.oakDark, 0.4);
    this.omdommeChipTekst = this.add
      .text(omdX + omdW / 2, radY + chipH / 2, 'Omdømme: Ny', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(12),
        color: '#3a2e22',
        fontStyle: '700',
      })
      .setOrigin(0.5);
  }

  /**
   * Mute-knapp og speed-knapp helt til høyre, samme y-rad som chip-er.
   * Mute kjører toggleMute → ambient + voice-over følger med (logikken
   * ligger inni audioManager.toggleMute som varsler voiceOver via
   * settEksternBlokkering).
   */
  private byggKnapper(): void {
    const padding = 22;
    const knappH = 48;
    const knappW = 56;
    const knappY = 10;

    // Runde 48 (Sven): tannhjul-knapp ytterst til høyre som åpner Settings;
    // lyd- og speed-knappen flyttet ett hakk innover.
    const settingsX = GAME_WIDTH - padding - knappW;
    this.settingsKnappBg = lagRundetRekt(this, settingsX, knappY, knappW, knappH, COLORS.white, 0.94, 12);
    this.settingsKnappBg.setStrokeStyle(1, COLORS.oakDark, 0.4);
    this.add
      .text(settingsX + knappW / 2, knappY + knappH / 2, '⚙', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#8b5e3c',
      })
      .setOrigin(0.5);
    this.settingsKnappBg.setInteractive({ useHandCursor: true });
    this.settingsKnappBg.on('pointerup', () => {
      sfx.play('click');
      document.dispatchEvent(new CustomEvent('hud:openSettings'));
    });

    const muteX = settingsX - 12 - knappW;
    this.muteKnappBg = lagRundetRekt(this, muteX, knappY, knappW, knappH, COLORS.white, 0.94, 12);
    this.muteKnappBg.setStrokeStyle(1, COLORS.oakDark, 0.4);
    this.muteKnappTekst = this.add
      .text(muteX + knappW / 2, knappY + knappH / 2, audioManager.erAv ? '🔇' : '🔊', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
      })
      .setOrigin(0.5);
    this.muteKnappBg.setInteractive({ useHandCursor: true });
    this.muteKnappBg.on('pointerup', () => this.toggleMute());

    const speedX = muteX - 12 - knappW;
    this.speedKnappBg = lagRundetRekt(this, speedX, knappY, knappW, knappH, COLORS.white, 0.94, 12);
    this.speedKnappBg.setStrokeStyle(1, COLORS.oakDark, 0.4);
    this.speedKnappTekst = this.add
      .text(speedX + knappW / 2, knappY + knappH / 2, autoSpeed.symbol(), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#8b5e3c',
      })
      .setOrigin(0.5);
    this.speedKnappBg.setInteractive({ useHandCursor: true });
    this.speedKnappBg.on('pointerup', () => {
      autoSpeed.cycle();
      sfx.play('click');
      // Symbol oppdateres i autoSpeed.subscribe-listener.
    });
  }

  // ---------- Oppdatering ----------

  /**
   * Mute-toggle er identisk med tidligere DOM-handler: state speiles i
   * audioManager (som varsler voice-over) og ambient-loop slås av/på.
   * Også HiddenActions sender hit via samme metode.
   */
  toggleMute(): boolean {
    const erAvNa = audioManager.toggleMute();
    if (erAvNa) ambientAudio.slaaAv();
    else ambientAudio.slaaPaa();
    this.muteKnappTekst?.setText(erAvNa ? '🔇' : '🔊');
    return erAvNa;
  }

  private oppdaterSpeedSymbol(_nivaa: AutoSpeedNivaa): void {
    this.speedKnappTekst?.setText(autoSpeed.symbol());
  }

  /**
   * Hvilefarge for kroner-telleren: vedvarende rød når saldoen er negativ
   * (du står på kassakreditt), ellers normal mørk. Slik blir «du er i minus»
   * en konstant tilstand man ser — ikke bare et blink.
   */
  private kronerHvileFarge(): string {
    return this.visKroner < 0 ? '#b3261e' : '#1a1a1a';
  }

  /**
   * Animerer kroner-telleren mot en ny verdi: tallet teller opp/ned over
   * ~450 ms, blinker grønt (økning) eller dempet rødt (minus) og fader
   * tilbake, og får en liten puls. Den viktigste tilbakemeldingen i spillet
   * — hver dag med salg, hvert kjøp — «smeller» nå i stedet for å hoppe.
   * Respekterer redusert bevegelse: da settes tallet direkte (men beholder
   * fargeblinket som ikke er bevegelse).
   */
  private oppdaterKroner(maal: number): void {
    // Første refresh (i create): sett direkte, ingen animasjon.
    if (!this.kronerInit) {
      this.kronerInit = true;
      this.visKroner = maal;
      this.kronerTekst?.setText(formaterKroner(maal));
      this.kronerTekst?.setColor(this.kronerHvileFarge());
      return;
    }

    if (maal === this.visKroner || !this.kronerTekst) {
      // Spilltest-funn: en løpende tween fra forrige endring kunne
      // overskrive teksten ETTER denne tidlige returen og la HUD-en
      // stå 2 000 kr feil i timevis. Stopp den alltid her.
      this.kronerTween?.stop();
      this.kronerTween = undefined;
      this.kronerTekst?.setText(formaterKroner(maal));
      this.visKroner = maal;
      this.kronerTekst?.setColor(this.kronerHvileFarge());
      return;
    }

    const fra = this.visKroner;
    const opp = maal > fra;
    this.visKroner = maal;

    // Den tydelige delen: en flytende «+1 240 kr» / «−X kr» som popper opp
    // under telleren. Dette er det øyet faktisk fanger — count-up av en stor
    // total er for subtil når dagsendringen er liten i forhold til saldoen.
    this.visKronerDelta(maal - fra);

    // Fargeblink — grønt opp / rødt ned — som så faller tilbake til
    // hvilefargen. Hvilefargen er rød når saldoen er negativ, så «på
    // kassakreditt» blir en vedvarende rød tilstand, ikke bare et blink.
    this.kronerFargeTimer?.remove();
    this.kronerTekst.setColor(opp ? '#2e7d32' : '#b3261e');
    this.kronerFargeTimer = this.time.delayedCall(700, () => {
      this.kronerTekst?.setColor(this.kronerHvileFarge());
    });

    if (foretrekkerRedusertBevegelse()) {
      this.kronerTekst.setText(formaterKroner(maal));
      return;
    }

    // Tell verdien opp/ned.
    this.kronerTween?.stop();
    const proxy = { v: fra };
    this.kronerTween = this.tweens.add({
      targets: proxy,
      v: maal,
      duration: 450,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        this.kronerTekst?.setText(formaterKroner(Math.round(proxy.v)));
      },
      onComplete: () => {
        this.kronerTekst?.setText(formaterKroner(maal));
      },
    });

    // Liten puls på selve tallet.
    this.kronerTekst.setScale(1);
    this.tweens.add({
      targets: this.kronerTekst,
      scale: 1.08,
      duration: 120,
      ease: 'Sine.easeOut',
      yoyo: true,
    });
  }

  /**
   * Flytende «+X kr» / «−X kr» rett under kroner-telleren. Popper opp i
   * grønt (inntekt) eller rødt (utgift), flyter oppover og fader ut. Dette
   * er den synlige kvitteringen på at en handling ga utslag — det count-up
   * av totalen ikke klarer alene.
   */
  private visKronerDelta(delta: number): void {
    if (delta === 0 || !this.kronerTekst) return;
    const opp = delta > 0;
    const tekst = `${opp ? '+' : '−'}${Math.abs(delta).toLocaleString('nb-NO')} kr`;
    const x = this.kronerTekst.x;
    const startY = HUD_HEIGHT + 14; // rett under HUD-stripen, i åpent rom

    const flyt = this.add
      .text(x, startY, tekst, {
        fontFamily: FONTS.display,
        fontSize: fontStr(16),
        color: opp ? '#2e7d32' : '#b3261e',
        fontStyle: '700',
      })
      .setOrigin(0, 0)
      .setDepth(50);

    if (foretrekkerRedusertBevegelse()) {
      this.tweens.add({
        targets: flyt,
        alpha: { from: 1, to: 0 },
        duration: 900,
        delay: 400,
        ease: 'Sine.easeIn',
        onComplete: () => flyt.destroy(),
      });
      return;
    }

    flyt.setAlpha(0);
    this.tweens.add({
      targets: flyt,
      y: startY - 22,
      alpha: { from: 1, to: 0 },
      duration: 1100,
      ease: 'Sine.easeOut',
      onComplete: () => flyt.destroy(),
    });
  }

  private refresh(snap: GameStateData): void {
    this.oppdaterKroner(snap.kroner);
    // Ny dag nullstiller GameClock (i ForlagshusetScene-subscriberen som
    // kjører før denne) — les klokka på nytt så HUD viser 08:00.
    this.oppdaterKlokke();
    this.dagTekst?.setText(String(snap.dag));
    this.sesongTekst?.setText(sesongTekst(snap.dag));

    // Tier-chip-farge + tekst
    if (this.tierChipBg && this.tierChipTekst) {
      this.tierChipBg.setFillStyle(TIER_BG[snap.tier], 1);
      this.tierChipBg.setStrokeStyle(2, COLORS.white, 0.9);
      this.tierChipTekst.setText(`Tier ${snap.tier}`);
      this.tierChipTekst.setColor(TIER_TEKST_FARGE[snap.tier]);
    }

    // Omdømme-chip
    if (this.omdommeChipTekst) {
      const nivaa = omdommeNivaaTekst(snap.omdomme);
      this.omdommeChipTekst.setText(`Omdømme: ${nivaa} (${snap.omdomme})`);
    }

    // Speil til ARIA-live-region for skjermlesere.
    ariaMirror.varselHud({
      kroner: snap.kroner,
      dag: snap.dag,
      sesong: SESONG_NAVN[sesongForDag(snap.dag)],
      tier: snap.tier,
    });
  }
}

// Beholdt for å unngå å brekke andre import-er som kanskje refererer
// hit (nivå-tekst). Samme verdi som internt brukes i HUD-en.
export function omdommeNivaa(verdi: number): string {
  return omdommeNivaaTekst(verdi);
}

// Eksporter TIER_COLORS-ref for testbarhet (uendret signatur).
export const HUD_TIER_FARGER = TIER_COLORS;
